'use client'

import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'
import { useLocalStorage } from 'usehooks-ts'

import { useAddressApiSwrBillingAddressMutation } from '../../address'
import { type OpenableControls, useOpenableControls } from '../../generic-hooks'
import { useRunCallbackWhenSessionCallIsDone } from '../../user-data'
import { useNowApiV4SwrGetBasicCheckMutation } from '../api/swr/nowApiV4SwrGetBasicCheck'
import { useIsNowEnabled } from '../components/useIsNowEnabled'

export type NowInfoStorage = {
  zipSupportInfo?: {
    checkedAt?: number
    inDeliveryRegion?: boolean
    zip?: string
  }
}

export type NowGeneralContextValue = {
  clearZipSupportInfo: () => void
  nowGeneralData: NowInfoStorage
  productPageNowDialogControls: OpenableControls
  setNowGeneralData: (nowInfoData: NowInfoStorage) => void
  updateZipSupportInfo: (data: NowInfoStorage['zipSupportInfo']) => void
}

const NowGeneralContext = createContext<NowGeneralContextValue | null>(null)

// 1000 * 60 * 60 * 24
const ONE_DAY_IN_MILLIS = 86_400_000

const checkWasFreshlyDone = (nowInfo: NowInfoStorage): boolean => {
  if (nowInfo.zipSupportInfo?.inDeliveryRegion === undefined) {
    return false
  }
  const { checkedAt } = nowInfo.zipSupportInfo

  if (checkedAt === undefined) {
    return false
  }

  return checkedAt > Date.now() - ONE_DAY_IN_MILLIS
}

const noZipWasSetBefore = (nowInfo: NowInfoStorage): boolean => {
  return !nowInfo.zipSupportInfo?.checkedAt
}

export const NowGeneralContextProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const productPageNowDialogControls = useOpenableControls()
  const isNowEnabled = useIsNowEnabled()

  const [nowGeneralData, setNowGeneralData] = useLocalStorage<NowInfoStorage>(
    'nowInfo',
    {},
    {
      initializeWithValue: false,
    },
  )

  const updateZipSupportInfo = useCallback(
    ({
      checkedAt,
      inDeliveryRegion,
      zip,
    }: NowInfoStorage['zipSupportInfo'] = {}) => {
      setNowGeneralData({
        zipSupportInfo: {
          checkedAt,
          inDeliveryRegion,
          zip,
        },
      })
    },
    [setNowGeneralData],
  )

  const fetchNowBasicCheckMutation = useNowApiV4SwrGetBasicCheckMutation({
    onSuccess: (response, zipCode): void => {
      updateZipSupportInfo({
        checkedAt: Date.now(),
        inDeliveryRegion: response.zipCodeInDeliveryRegion,
        zip: zipCode,
      })
    },
    swrKeyNonce: 'zipCheck',
  })

  const fetchBillingAddressMutation = useAddressApiSwrBillingAddressMutation({
    onSuccess: (data) => {
      if (!data?.zip) {
        return
      }
      void fetchNowBasicCheckMutation.trigger({ zipCode: data.zip })
    },
  })

  const sessionCallDoneIsAlreadyHandled = useRef(false)
  useRunCallbackWhenSessionCallIsDone((data) => {
    if (!isNowEnabled) {
      return
    }

    // logic has run already - skip, otherwise will run on every rerender
    if (sessionCallDoneIsAlreadyHandled.current) {
      return
    }
    // mark that has already run
    sessionCallDoneIsAlreadyHandled.current = true

    // check was performed already in previous session, or check was done more than specific time - skip
    if (checkWasFreshlyDone(nowGeneralData)) {
      return
    }

    // for logged in fetch billing address and check with it's address if present
    if (Boolean(data?.customerNumber) && noZipWasSetBefore(nowGeneralData)) {
      void fetchBillingAddressMutation.trigger()

      return
    }

    // for not logged in, in case zip is present in storage (e.g. through form) - check
    if (nowGeneralData.zipSupportInfo?.zip) {
      void fetchNowBasicCheckMutation.trigger({
        zipCode: nowGeneralData.zipSupportInfo.zip,
      })
    }
  })

  const value = useMemo(() => {
    return {
      clearZipSupportInfo: (): void => {
        setNowGeneralData({ zipSupportInfo: {} })
      },
      nowGeneralData,
      productPageNowDialogControls,
      setNowGeneralData,
      updateZipSupportInfo,
    }
  }, [
    nowGeneralData,
    setNowGeneralData,
    updateZipSupportInfo,
    productPageNowDialogControls,
  ])

  return (
    <NowGeneralContext.Provider value={value}>
      {children}
    </NowGeneralContext.Provider>
  )
}

export const useNowGeneralContext = (): NowGeneralContextValue => {
  const contextValue = useContext(NowGeneralContext)

  if (!contextValue) {
    throw new Error(
      'useNowGeneralContext must be used within NowGeneralContextProvider',
    )
  }

  return contextValue
}
